import { tracker } from '@lessonup/analytics';
import { AuthStatus, DataLoader, logger, useAuth, useDocumentQuery } from '@lessonup/client-integration';
import { styled } from '@lessonup/ui-components';
import { compact } from 'lodash';
import React, { useState } from 'react';
import { useFeatureFlags } from '../../utils/featureFlags/useFeatureFlags';
import { ReportDocument } from './ReportPageFeature.graphql.generated';
import { ExportButton } from './components/ExportButton/ExportButton';
import { ReportDetails, ReportDetailsProps } from './components/ReportDetails/ReportDetails';
import { ReportUnavailable } from './components/ReportUnavailable/ReportUnavailable';

export type ReportPageFeatureProps = {
  reportId?: string;
};

export function ReportPageFeature({ reportId }: ReportPageFeatureProps) {
  const auth = useAuth();

  const { isFeatureEnabled } = useFeatureFlags();
  const featureFlag = isFeatureEnabled('reportsV2');
  const [reportOpenTracked, setReportOpenTracked] = useState(false);

  const { data, loading, error } = useDocumentQuery(ReportDocument, {
    fetchPolicy: 'cache-first',
    skip: !reportId || auth?.type !== AuthStatus.LOGGED_IN,
    variables: {
      reportId: reportId || '', // `reportId` should always be defined because of the check for `skip`
    },
    onCompleted(data) {
      const { lessonId, assignmentId } = data?.viewer?.reportById || {};
      if (!lessonId || !assignmentId) {
        logger.error('Cannot fire reportOpen event because of missing data', { lessonId, assignmentId });
        return;
      }

      if (reportOpenTracked) return;

      tracker.events.reportOpen({
        assignmentId,
        lessonId,
        editorVersion: 'v2',
      });

      setReportOpenTracked(true);
    },
  });

  return (
    <StyledDataLoader
      data={data}
      error={error}
      customErrorRenderer={ReportUnavailable}
      dataRenderer={(data) => {
        const reportById = data.viewer.reportById;
        if (!featureFlag || !reportById) return <ReportUnavailable />;

        const report: ReportDetailsProps['report'] = {
          id: reportById.id || '',
          taughtAt: new Date(reportById.taughtAt),
          lesson: {
            name: reportById.lessonName || '',
          },
          studentReports: reportById.studentReports.map((studentReport) => ({
            id: studentReport.id,
            name: studentReport.name,
          })),
          numberOfStudents: reportById.studentReports.length || 0,
          questionReports: compact(
            reportById.questionReports.map(
              ({
                id,
                questionTitle,
                slideNumber,
                questionType,
                percentageStudentsAnsweredCorrectly,
                percentageStudentsAnsweredIncorrectly,
                percentageStudentsUnanswered,
              }) => ({
                id,
                questionTitle,
                slideNumber,
                questionType,
                percentageStudentsAnsweredCorrectly: percentageStudentsAnsweredCorrectly ?? null,
                percentageStudentsAnsweredIncorrectly: percentageStudentsAnsweredIncorrectly ?? null,
                percentageStudentsUnanswered,
              })
            )
          ),
        };

        return <ReportDetails report={report} exportButton={<ExportButton reportId={report.id} />} />;
      }}
      loading={loading}
    />
  );
}

const StyledDataLoader = styled(DataLoader)`
  width: 100%;
  height: 100%;
` as typeof DataLoader;
